// Document (DMS) approvals inside the generic "My Approvals" inbox.
//
// A document repository routes its release sign-off through the SAME approval
// engine transactions use — same levels, same roles, same table — so its
// instances arrive in this inbox alongside transaction ones. Everything about
// them is the same EXCEPT what promotion means: a transaction writes a record,
// a document moves one version pointer (`versioning.promote`), which only the
// repository knows how to do.
//
// The inbox therefore has to tell the two apart before it does anything:
//   • there is no transaction model behind `DMS_<repo>`, so loading one only
//     ever produced "No transaction model found";
//   • the staged header is a POINTER (`{"dms": {document_id, …}}`), not a
//     record, so the generic field preview rendered `[object Object]`;
//   • Edit is meaningless — an approver reviews the real file, they do not
//     retype a staged copy of it;
//   • and the decision must go to `/dms/approvals/decide`, not to the
//     transaction path (which now refuses it by name rather than failing).
//
// Detection keys on the staged PAYLOAD SHAPE rather than the object name: a
// real transaction could legitimately be called `DMS_SOMETHING`, but only a
// document approval carries a `dms` / `dms_review` pointer.

import type { ApprovalInstance } from '../contracts'

/** What a document approval stages. `''` when the instance is not one. */
export type DmsApprovalKind = '' | 'version' | 'review'

/** The synthetic object-name prefix `approval_bridge.obj_name_for` builds. */
const OBJ_PREFIX = 'DMS_'

function payloadOf(inst: ApprovalInstance | null | undefined,
                   key: string): Record<string, unknown> | null {
  let raw: unknown = inst?.header_json
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw) } catch { return null }
  }
  if (!raw || typeof raw !== 'object') return null
  const body = (raw as Record<string, unknown>)[key]
  return body && typeof body === 'object' ? body as Record<string, unknown> : null
}

export function dmsKindOf(inst: ApprovalInstance | null | undefined): DmsApprovalKind {
  const version = payloadOf(inst, 'dms')
  if (version && version.document_id) return 'version'
  const review = payloadOf(inst, 'dms_review')
  if (review && review.review_id) return 'review'
  return ''
}

export function isDmsInstance(inst: ApprovalInstance | null | undefined): boolean {
  return dmsKindOf(inst) !== ''
}

/**
 * The repository object name a DMS instance belongs to — the `repo` every
 * `/dms/*` call is scoped by. Empty when the instance is not a document one.
 */
export function dmsRepoOf(inst: ApprovalInstance | null | undefined): string {
  if (!isDmsInstance(inst)) return ''
  const obj = String(inst?.obj_name || '')
  return obj.startsWith(OBJ_PREFIX) ? obj.slice(OBJ_PREFIX.length) : obj
}

/**
 * A document SUBMITTED for a sign-off that nobody has raised yet.
 *
 * The repository creates its approval instance when the reviewer invokes the
 * action, not when the author submits — so between those two moments there is
 * no instance, and a submissions list that reads the approval store showed the
 * author nothing for a document that really is in review. The server fills the
 * window from the worklist task that already tracks it.
 *
 * `id` on such a row is a TASK KEY, not an instance id: nothing may fetch,
 * decide, edit or withdraw it, and the inbox opens the document instead.
 */
export function pendingSignoffOf(inst: ApprovalInstance | null | undefined) {
  const p = inst?.pending_signoff
  return p && p.document_id ? p : null
}

/**
 * What the list row says a document approval IS, before its title has been
 * resolved. Never the raw pointer — an approver reading `[object Object]`
 * learns nothing, and an approver reading nothing at least learns that.
 */
export function dmsRowSubtitle(kind: DmsApprovalKind): string {
  return kind === 'review'
    ? 'Review comments awaiting sign-off'
    : 'Document release awaiting sign-off'
}
